<template>
    <Catalog v-if="catalogView" />
    <div v-else id="app">
        <InFrame v-if="openMoment.visible" @close="closeOpenMoment" :url="openMoment.url" :size="openMoment.size" />
        <div v-if="showReview">
            <h2 style="color: white; text-align: center; padding-top: 50%; transform: translateY(-50%)">
                Puanlamanız için teşekkür ederiz.
            </h2>
        </div>
        <div v-else>
            <ChatV2
                :key="chatKey"
                :messages="getMessages()"
                :onClosed="chatClosed"
                :participants="getParticipants()"
                :sendMessage="sendMessage"
                :sendTyping="sendTyping"
                :chatbot-active="$_.get(conversation, 'status') === 'chatbot'"
            />
        </div>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import "@/assets/scss/app.scss";
import "@/assets/scss/material.scss";
import "vue-material/dist/theme/default.css";
import request from "./utils/request";
import ChatV2 from "./components/V2/Chat";
import DateUtils from "./utils/DateUtil";
import * as _ from "lodash";
import { log2 } from "./utils/log2";
import { getPersistenceData, removePersistenceData, setPersistenceData } from "./utils/persistenceStorage";

import {
    onMobileVersionDetected,
    parentWindowMessageStream,
    requestHostUrl,
    requestKMKSellerId,
    sendCommandToParentWindow,
    sendOpenBubbleRequest,
} from "./utils/parentWindowConnection";
import { sessionId } from "./utils/session";
import { rateOSR } from "./api/offlineSupportRequest";
import { addSocketMessage, sendSocketMessage, socketMessageStream, updateSocketRef } from "./utils/socketStream";
import version from "./utils/version";
import io from "socket.io-client";
import { getNextSocketServerCandidate, markFailedSocketServer } from "./api/hostFetcher";
import { Subject } from "rxjs";
import { filter, first, map } from "rxjs/operators";
import { publishMessage, systemMessageStream, SystemMessageTypes } from "./utils/systemPubSub";
import { reportInfoJob, reportUrlJob } from "./jobs/reportUrlJob";
import { PHONE } from "./webrtc/phoneSDK";
import { PHONE_UTILS } from "./utils/phone";
import { setThemeColors } from "./utils/setThemeColors";
import { trackKmkSellerId } from "./jobs/trackKmkSellerId";
import * as Sentry from "@sentry/vue";
import Catalog from "./components/catalog/Catalog.vue";
import { Trigger } from "./utils/trigger";
import { CONFIG } from "./utils/config";
import AkinonScrapperApiClient from "./utils/AkinonScrapperApiClient";
import InFrame from "./components/V2/popup-chat/components/InFrame.vue";
import { waitForChatAgents } from "./components/util/waitForChatAgents";
import store from "./store";

const log = log2("App");
const preDefinedPendingMessages = () => JSON.parse(getPersistenceData("pending-messages", "[]"));
export default {
    name: "App",
    components: {
        InFrame,
        Catalog,
        ChatV2,
    },
    data() {
        return {
            chatKey: 0,
            disconnected: false,
            currentUrl: window.location.hostname,
            socketConnected: false,
            visitorInfo: {
                email: "",
                name: "",
                subjectId: "",
            },
            message: "",
            messages: [],
            pendingMessages: Array.isArray(preDefinedPendingMessages()) ? preDefinedPendingMessages() : [],
            emptyMessages: [],
            showReview: false,
            $socket: undefined,
            connectedSocketServerId: undefined,
            socketTryCount: 0,
            visitorMessageStream: new Subject(),
            cancels: [],
            lastKMKTrackerSub: undefined,
            subjects: [],
            conversation: null,
            catalogView: false,
            openMoment: {
                url: null,
                visible: false,
                size: "full",
            },
            newVersion: true,
        };
    },
    destroyed() {
        if (this.scFixerTimer) {
            clearInterval(this.scFixerTimer);
        }
        if (this.urlChangeSub) his.urlChangeSub.unsubscribe();
    },
    created() {
        this.initializeTheme();
        this.rating();
        this.catalog();

        const self = this;

        onMobileVersionDetected().then((payload) => {
            self.setMobileVersion(true);
            if (payload.isSafari) self.setSafariMode(payload.isSafari);
        });

        this.currentUrl = window.location.hostname;

        this.conversationId = getPersistenceData("conversationId");

        requestHostUrl().then((url) => {
            this.setCurrentHostUrl(url);
        });

        if (this.newVersion) {
            document.body.classList.add("v2");
            document.body.classList.remove("v1");
        } else {
            document.body.classList.add("v1");
            document.body.classList.remove("v2");
        }
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            language: (state) => state.language.language,
            languages: (state) => state.language.languages,
            settings: (state) => state.setting.settings,
            offlineMode: (state) => state.setting.offlineMode,
            visitorId: (state) => state.chat.visitorId,
            preferredThemeColor: (state) => state.setting.settings.chatCustomizationSettings.preferredThemeColor,
            preferredAnimation: (state) => state.setting.settings.chatCustomizationSettings.preferredAnimation,
            allThemeColors: (state) => state.setting.allThemeColors,
            allAnimations: (state) => state.setting.allAnimations,
            safari: (state) => state.app.safari,
            mobileVersion: (state) => state.app.mobileVersion,
            socketIsOpen: (state) => state.chat.socketIsOpen,
            visitorIsLogging: (state) => state.chat.visitorIsLogging,
            visitorIsLoggedIn: (state) => state.chat.visitorIsLoggedIn,
            queueNumber: (state) => state.chat.queueNumber,
            chatAgents: (state) => state.chat.chatAgents,
            connected: (state) => state.chat.connected && !state.app.showAfterActions,
            isSoundOnly: (state) => state.webrtc.isSoundOnly,
            tabVisible: (state) => state.app.tabVisible,
            previewMode: (state) => state.app.previewMode,
            lastTabHiddenTS: (state) => state.app.lastTabHiddenTS,
            loginFormRequired: (state) => {
                const loginFormRequired = state.setting.settings.loginFormRequired;
                const customLoginFormEnabled = state.setting.settings.customLoginFormEnabled;
                const customLoginFormIsValid = !!state.customLoginForm.inputs.filter((x) => x.visible).length;
                if (customLoginFormEnabled && !customLoginFormIsValid) {
                    return false;
                } else {
                    return loginFormRequired;
                }
            },
            customLoginFormEnabled: (state) => {
                const enabledFlag = state.setting.settings.customLoginFormEnabled;
                const isValid = !!state.customLoginForm.inputs.filter((x) => x.visible).length;
                return enabledFlag && isValid;
            },
            preSelectedDepartment: (state) => state.chat.preSelectedDepartment,
            visitingPage: (state) => state.app.visitingPage,
            preDefinedUserData: (state) => state.chat.preDefinedUserData,
            preferredChatbotStartBlockId: (state) => state.chat.preferredChatbotStartBlockId,
            contactPropertyData: (state) => state.chat.contactPropertyData,
            visitorInteracted: (state) => state.app.visitorInteracted,
        }),
    },
    methods: {
        ...mapActions({
            getSiteByCustomerClientAddress: "site/getSiteByCustomerClientAddress",
            getSettings: "setting/getSettings",
            getCustomLoginFormInputs: "customLoginForm/getCustomLoginFormInputs",
            setLanguage: "language/setLanguage",
            connectChat: "chat/connectChat",
            disconnectChat: "chat/disconnect",
            setVisitor: "chat/setVisitor",
            setConnectionStatus: "chat/setConnectionStatus",
            toggleAfterActions: "app/toggleAfterActions",
            setCurrentHostUrl: "app/setCurrentHostUrl",
            toggleCloseChatConfirmation: "app/toggleCloseChatConfirmation",
            setMobileVersion: "app/setMobileVersion",
            setSafariMode: "app/setSafariMode",
            setSettings: "setting/setSettings",
            setVersion: "setting/setVersion",
            setVisitorIsLogging: "chat/setVisitorIsLogging",
            setVisitorIsLoggedIn: "chat/setVisitorIsLoggedIn",
            setQueueNumber: "chat/setQueueNumber",
            setChatAgents: "chat/setChatAgents",
            offlineModeUpdated: "setting/offlineModeUpdated",
            multiSetInputs: "customLoginForm/multiSetInputs",
            changeCatalogContent: "catalog/changeCatalogContent",
            changeCatalogCategory: "catalog/changeCatalogCategory",
            setSocket: "app/setSocket",
            setVideoCallMode: "app/setVideoCallMode",
            setCalling: "app/setCalling",
            setVideoCall: "app/setVideoCall",
            setSoundOnly: "webrtc/setSoundOnly",
            setScreenOnly: "webrtc/setScreenOnly",
            closeConnection: "webrtc/closeConnection",
            changeTabVisibilityChange: "app/changeTabVisibilityChange",
            setChatbotActiveState: "chat/setChatbotActiveState",
            setPreDefinedUserData: "chat/setPreDefinedUserData",
            setVisitingPage: "app/setVisitingPage",
            setVisitorLocation: "app/setLocation",
            getCountryPhoneInfos: "app/getCountryPhoneInfos",
            setVisitorInteracted: "app/setVisitorInteracted",
        }),
        ...mapMutations({
            mutRemoteVideoEnabled: "webrtc/SET_REMOTE_VIDEO_ENABLED",
            mutRemoteMicEnabled: "webrtc/SET_REMOTE_MIC_ENABLED",
            updateUserLang: "chat/UPDATE_USER_LANG",
            setVisitorInfo: "chat/SET_VISITOR_INFO",
            updateAgents: "chat/UPDATE_CHAT_AGENTS",
            setVisitorId: "chat/SET_VISITOR_ID",
            setPreferredChatbotStartBlockId: "chat/SET_PREFERRED_CHATBOT_START_BLOCK_ID",
        }),
        catalog() {
            let queryParams = new URLSearchParams(window.location.search);
            let catalog = queryParams.get("catalog");
            if (!!catalog) {
                this.catalogView = true;
            }
        },
        rating() {
            let queryParams = new URLSearchParams(window.location.search);
            let id = queryParams.get("id");
            let point = parseInt(queryParams.get("point"));
            let siteId = queryParams.get("siteId");
            let rateTicket = queryParams.get("rateTicket");
            log("rate log is here ", { id, point, siteId });
            if (id && point && siteId) {
                this.showReview = true;
                rateOSR({ id, point, siteId }).then(() => {
                    setTimeout(() => {
                        this.showReview = false;
                        window.location.search = "";
                    }, 1000 * 5 /* 5 sn sonra customer clienti göster */);
                });
            }
            if (rateTicket) {
                this.showReview = true;
            }
        },
        get(sendSignal = false) {
            const getSiteByCustomerClientAddress = (resolve) => {
                return this.getSiteByCustomerClientAddress(this.currentUrl).then((response) => {
                    this.getSettings().then(() => {
                        if (sendSignal && !(this.settings.offlineMode && this.settings.autoHideBubble)) {
                            log("connected sending to parent from 1");
                            sendCommandToParentWindow("connected");
                        } else if (!this.settings.hideBubble) {
                            log("connected sending to parent from 2");
                            sendCommandToParentWindow("connected");
                        }
                        if (this.settings.language.customerClientLanguageDetect === true) {
                            const userLang = navigator.language || navigator.userLanguage;
                            let languages = this.languages.filter((w) => userLang.indexOf(w.code) !== -1);
                            if (languages.length > 0) {
                                this.setLanguage(languages[0].code);
                                this.$i18n.locale = this.language.code;
                            } else {
                                this.setLanguage(this.settings.language.customerClientDefault);
                                this.$i18n.locale = this.language.code;
                            }
                        } else {
                            log(
                                "Customerclientdefault language is here ",
                                this.settings.language.customerClientDefault
                            );
                            this.setLanguage(this.settings.language.customerClientDefault);
                            this.$i18n.locale = this.language.code;
                        }

                        if (this.settings.customLoginFormEnabled) {
                            this.getCustomLoginFormInputs();
                        }
                        this.getCountryPhoneInfos();
                    });
                    publishMessage("site-id-collected", { siteId: response._id });
                    log("this.$socket", this.$socket, this);
                    resolve();
                });
            };
            return new Promise((resolve) => {
                getSiteByCustomerClientAddress(resolve).catch((err) => {
                    log.error("customer site fetch error", err);
                    setTimeout(() => {
                        this.get().then(resolve);
                    }, Math.floor(Math.random() * 10 * 1000) + 1000);
                });
            });
        },
        sendVisitorConnectRequest() {
            if (this.socketConnected) return;
            this.socketConnected = true;

            sendSocketMessage("visitor connect", {
                visitorId: this.visitorId,
                conversationId: this.conversationId,
                siteId: this.site._id,
                sessionId,
                version,
                navLang: navigator.language,
            });
        },
        dateToHour(date) {
            const d = new Date(date);
            return `${d.getHours()}:${("0" + d.getMinutes()).slice(-2)}`;
        },
        disconnect(sendSocket = true) {
            log("disconnect", "sendSocket=", sendSocket);
            if (sendSocket) {
                sendSocketMessage("visitor destroy", {
                    visitorId: this.visitorId,
                    conversationId: this.conversationId,
                    siteId: this.site._id,
                });
                sendCommandToParentWindow("visitor-disconnected");
            }

            sendCommandToParentWindow("wide-visitor-screen", false);
            this.disconnectChat();
            this.toggleCloseChatConfirmation(false);
            this.toggleAfterActions(true);

            this.stopHealthCheckTimer();
            this.conversationId = undefined;
            removePersistenceData("conversationId");
            this.chatClosed();
            if (this.visitorIsLogging) {
                this.setVisitorIsLogging(false);
                this.setVisitorIsLoggedIn(false);
            }
        },
        chatClosed() {
            log("chatClosed");
            Sentry.setTags({
                conversationId: undefined,
            });
            sendCommandToParentWindow("wide-visitor-screen", false);
            this.$store.dispatch("chatbot/setFeedBack", null);
            /*this.resetState();*/
            this.stopHealthCheckTimer();
            this.conversationId = undefined;
            removePersistenceData("conversationId");
            const phone = PHONE_UTILS.getPhone();
            if (phone) {
                Object.keys(phone.conversations).forEach((number) => phone.hangup(number));
                phone.camera.stop();
                this.setVideoCallMode(false);
                this.setVideoCall(null);
                this.setCalling(null);
                this.closeConnection();
            }
            if (!this.loginFormRequired) {
                this.setVisitorIsLogging(false);
                this.setVisitorIsLoggedIn(false);
            }
            this.pendingMessages = [];
            removePersistenceData("pending-messages");
            this.setVisitorIsLoggedIn(false);
            this.setVisitorIsLogging(false);
        },
        resetState() {
            log("resetState called");
            this.conversationId = undefined;
            this.visitorInfo = {
                email: "",
                name: "",
                subjectId: "",
            };
            this.message = "";
            this.messages = [];
            this.emptyMessages = [];
            this.stopHealthCheckTimer();
            this.setChatAgents([]);
            removePersistenceData("conversationId");
        },
        async getPreSelectedSubjectId() {
            log("getPreSelectedSubjectId", { preSelectedDepartment: this.preSelectedDepartment });
            if (this.preSelectedDepartment) {
                const { data } = await request.get("/public/subjects/get-all");
                const foundDepartment = data.find((x) =>
                    x.languageContent.map((x) => x.name).includes(this.preSelectedDepartment)
                );
                log("getPreSelectedSubjectId", { foundDepartment, all: data });
                if (foundDepartment) return foundDepartment._id;
                else return undefined;
            }
            return undefined;
        },
        addMessage(data) {
            log("new message adding", data);
            if (data.type === "rep-typing") return;
            let items = [...this.messages, data];
            items = _.orderBy(
                _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                (q) => new Date(q.createdDate).getTime(),
                "asc"
            );
            this.messages = items;
        },
        sendLogin(payload, ignoreSource) {
            log(`sendLogin called from ${ignoreSource}`, payload);
            if (this.visitorIsLoggedIn) {
                log.error("send login disabled , already logged in");
                return;
            }
            if (this.visitorIsLogging) {
                log.error("send login disabled , another login precess in queue");
                return;
            }
            payload["chatbotStartBlockId"] = this.preferredChatbotStartBlockId;
            if (!!Object.keys(this.contactPropertyData).length) {
                payload["contactPropertyData"] = this.contactPropertyData;
            }
            payload.referrer = document.referrer || "";
            this.setVisitorIsLogging(true);
            sendSocketMessage("visitor login", payload);
        },
        bindAkinonParams(params, akinonUserData) {
            params.akinonUserData = akinonUserData;
            const akinonEmail = _.get(akinonUserData, "user.email", null);
            const akinonName = _.get(akinonUserData, "user.name", null);
            if (akinonEmail) {
                params.email = params.email || akinonEmail;
                params.fullname = params.fullname || akinonName;
            }
            return params;
        },
        async calculatePredefinedUserData(autoLoginData) {
            let preDefined = { email: undefined, fullname: undefined };
            try {
                const { email, fullname, initialized } = this.preDefinedUserData;
                if (initialized) preDefined = { email, fullname };
            } catch (e) {}
            let subjectId = await this.getPreSelectedSubjectId().catch(() => undefined);
            let params = {};
            const queryParams = this.calculateQueryParams();
            if (Object.keys(queryParams).length > 0) {
                Object.keys(queryParams).forEach((key) => {
                    params[key] = queryParams[key];
                });
            }
            if (preDefined.email) params.email = preDefined.email;
            if (preDefined.fullname) params.fullname = preDefined.fullname;
            if (subjectId) params.subjectId = subjectId;
            if (CONFIG.AKINON_ENABLED(this.visitingPage)) {
                const akinonScrapperApiClient = AkinonScrapperApiClient.create();
                const akinonUserData = await akinonScrapperApiClient.getUserData();
                if (akinonUserData) {
                    params = this.bindAkinonParams(params, akinonUserData);
                }
            }
            if (!!_.get(autoLoginData, "akinonUserData")) {
                params = this.bindAkinonParams(params, _.get(autoLoginData, "akinonUserData"));
            }
            log("calculated predefined data", params);
            return params;
        },
        calculateQueryParams() {
            const queryParams = new URLSearchParams(window.location.search);
            const params = {};
            for (const [key, value] of queryParams.entries()) {
                params[key] = value;
            }
            return params;
        },
        async sendMessage(
            messageText,
            isPending = false,
            isChatBotMenu = false,
            isAutoLoginInitialMessage = false,
            autoLoginData = {}
        ) {
            let formattedText = messageText.replace(/(?:\r\n|\r|\n)/g, "<br>");
            log("sendMessage called", {
                loginFormRequired: this.loginFormRequired,
                conversationId: this.conversationId,
                connected: this.connected,
                pendingMessages: this.pendingMessages,
                isPending,
                formattedText,
                isAutoLoginInitialMessage,
                autoLoginData,
            });
            if ((!this.loginFormRequired && !this.conversationId && !this.connected) || isAutoLoginInitialMessage) {
                if (isPending) return;
                const message = {
                    _id: this.pendingMessages.length,
                    seen: false,
                    createdDate: Date.now(),
                    uniqMessageId: this.pendingMessages.length,
                    description: formattedText,
                    messageType: "visitor",
                    systemMessageType: "unknown",
                    user: null,
                    asset: null,
                    createdBy: null,
                    updatedBy: null,
                };
                this.pendingMessages = [...this.pendingMessages, message];
                setPersistenceData("pending-messages", JSON.stringify(this.pendingMessages), true);
                this.addMessage(message);
                if (this.pendingMessages.length === 1) {
                    let preDefined = await this.calculatePredefinedUserData(autoLoginData);

                    const loginPayload = {
                        visitorId: this.visitorId,
                        languageCode: this.language.code,
                        siteId: this.site._id,
                        visitingPage: this.visitingPage,
                        loginless: true,
                        ...preDefined,
                        ...autoLoginData,
                        sessionId,
                        connectingUserId: autoLoginData?.connectingUserId,
                    };
                    this.sendLogin(loginPayload, autoLoginData ? "autoLogin" : "first pending message");
                }
            } else {
                sendSocketMessage("visitor message", {
                    conversationId: this.conversationId,
                    visitorId: this.visitorId,
                    message: formattedText,
                    siteId: this.site._id,
                    chatBotEnable: _.get(this.conversation, "status") === "chatbot",
                    isChatBotMenu,
                });
            }
            this.visitorMessageStream.next(formattedText);
            this.message = "";
            if (this.typingTimerId) {
                clearTimeout(this.typingTimerId);
            }
        },
        sendTyping(msg) {
            if (this.settings.writing && this.connected) {
                const payload = {
                    conversationId: this.conversationId,
                    visitorId: this.visitorId,
                    message: msg,
                    siteId: this.site._id,
                };
                if (this.typingTimerId) {
                    clearTimeout(this.typingTimerId);
                }
                this.lastTypingTS = Date.now();
                this.typingTimerId = setTimeout(() => {
                    sendSocketMessage("visitor typing", payload);
                }, 750);

                // if (!this.lastTypingTS || Date.now() - this.lastTypingTS > 1000) {
                //     sendSocketMessage("visitor typing",payload);
                //     this.lastTypingTS = Date.now();
                // }
            }
        },
        setVisitorContactProperty(payload) {
            log("setVisitorContactProperty", payload);
            if (!!Object.keys(payload).length && this.visitorId)
                sendSocketMessage("visitor contact-property", {
                    visitorId: this.visitorId,
                    siteId: this.site._id,
                    payload,
                });
        },
        setVipCustomer(payload) {
            log("setVipCustomer", payload);
            sendSocketMessage("visitor vip-customer", {
                visitorId: this.visitorId,
                siteId: this.site._id,
                payload,
            });
        },
        getMessages() {
            const previewMessages = [
                {
                    description: "Hi , I've a question",
                    createdDate: Date.now(),
                    uniqMessageId: 1,
                    messageType: "visitor",
                },
                {
                    description: "Hi Olivia , how can i help you",
                    createdDate: Date.now(),
                    uniqMessageId: 2,
                    messageType: "user",
                    userId: "John Doe",
                },
                {
                    description: "What's my last order status",
                    createdDate: Date.now(),
                    uniqMessageId: 3,
                    messageType: "visitor",
                },
                {
                    description: "Your order has shipped",
                    createdDate: Date.now(),
                    uniqMessageId: 4,
                    messageType: "user",
                    userId: "John Doe",
                },
            ];
            const items = this.previewMode ? previewMessages : this.messages;
            // log("getMessages", { messages: items });
            console.log("items...", items);
            const allMessages = items
                .map((x, i) => {
                    let type = "";
                    let author = "";
                    if (x.messageType === "system") {
                        type = "system";
                    } else if (["user", "chatbot"].includes(x.messageType)) {
                        type = "text";
                        author = x.userId || "Chatbot";
                    } else if (x.messageType === "user-email") {
                        x.message = "Mail gönderildi";
                        type = "system";
                    } else if (x.messageType === "user-sms") {
                        x.message = "SMS gönderildi";
                        type = "system";
                    } else if (x.messageType.includes("chatbot")) {
                        type = x.messageType;
                        author = "Chatbot";
                    } else if (x.messageType === "private") {
                        type = "private";
                    } else if (x.messageType === "visitor") {
                        type = "text";
                        author = "me";
                    } else if (x.messageType === "user-media" || x.messageType === "visitor-media") {
                        author = x.messageType === "user-media" ? x.userId : "me";
                        const mime = x.asset?.mime || "";
                        const ext = x.asset?.ext || "";
                        if ((!!mime && mime.includes("image")) || ["jpg", "jpeg", "png"].includes(ext)) {
                            type = "image";
                        } else if ((!!mime && mime.includes("audio")) || ext.includes("mp3")) {
                            type = "audio";
                        } else {
                            type = "asset";
                        }
                    } else if (x.messageType === "visitor-location") {
                        author = x.messageType === "visitor-location" ? "me" : x.userId;
                        type = "location";
                    } else if (x.messageType === "user-location") {
                        author = x.messageType === "user-location" ? x.userId : "me";
                        type = "location";
                    } else if (x.messageType.startsWith("chatbot")) {
                        type = x.messageType;
                        author = "chatbot";
                    }
                    return {
                        type,
                        author,
                        data: {
                            text: x.message || x.description || _.get(x, "payload.text"),
                            meta: DateUtils.formatHour(x.createdDate),
                        },
                        raw: x,
                    };
                })
                .filter((x) => x !== undefined);
            // log("allMessages", allMessages);
            return allMessages;
        },
        getParticipants() {
            if (_.get(this.conversation, "status") === "chatbot") {
                const chatbotName = _.get(this.settings, "chatbotName", CONFIG.CHATBOT_NAMES.default);
                return [{ name: chatbotName, _id: "chatbot" }];
            } else {
                return _.uniqBy(this.chatAgents, (q) => q._id);
            }
        },
        loadOldMessages(resetIfClosed = true, conversationId) {
            // log("loadOldMessages called", {
            //     conversationId: this.conversationId,
            //     messages: this.messages,
            // });
            conversationId = conversationId || this.conversationId;
            if (conversationId && this.connected) {
                return request({
                    url: `/conversations/${conversationId}/messages`,
                    method: "get",
                }).then((response) => {
                    const messages = [];
                    let items = response.data.data;
                    const oldMessages = [...this.messages];
                    if (oldMessages.length !== items.length) items = [...items, ...oldMessages];
                    items = _.orderBy(
                        _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                        (q) => new Date(q.createdDate).getTime(),
                        "asc"
                    );
                    let resetNeeded = false;
                    for (const data of items) {
                        if (data.type === "customer-typing") continue;

                        if (resetIfClosed) {
                            if (
                                "user-destroy" === data.systemMessageType ||
                                "visitor-destroy" === data.systemMessageType
                            ) {
                                resetNeeded = true;
                                this.resetState();
                                break;
                            }
                        }

                        messages.push(data);
                    }
                    log.debug("old messages", { messages });
                    this.messages = messages;
                    if (resetNeeded) this.resetState();
                });
            }
        },
        sendHealthCheck() {
            const diff = this.lastHealthCheckTS ? Date.now() - this.lastHealthCheckTS : null;
            log.debug("HC:send diff(ms): " + diff, {
                conversationId: this.conversationId,
                visitorId: this.visitorId,
            });
            if (this.conversationId && this.visitorId) {
                sendSocketMessage("visitor health_check", {
                    conversationId: this.conversationId,
                    siteId: this.site._id,
                    visitorId: this.visitorId,
                });
            }
            this.lastHealthCheckTS = Date.now();
        },
        startHealthCheckTimer() {
            log.debug("HC:start");
            this.sendHealthCheck();
            this.healthCheckTimerId = setInterval(() => {
                //log('health_check sending', { conversationId: this.conversationId })

                this.sendHealthCheck();
            }, 10000);
        },
        stopHealthCheckTimer() {
            log.debug("HC:stop");
            if (this.healthCheckTimerId) {
                clearInterval(this.healthCheckTimerId);
                this.healthCheckTimerId = undefined;
            }
        },
        init(sendSignal) {
            log("init", { visitorId: this.visitorId });
            const p = this.get(sendSignal);
            p.then((x) => {
                log("socket.io instance", this.$socket);
                this.startSocketIOConnection();
            });
            return p;
        },
        async startChatImmediately(connectingUserId) {
            log("startChatImmediately", { connectingUserId });
            if (!this.connected) {
                this.toggleAfterActions(false);

                const lastLoginData = JSON.parse(getPersistenceData("lastLoginData") || null);
                const lastCustomLogin = JSON.parse(getPersistenceData("lastCustomLogins") || "[]")[0];
                let preDefined = await this.calculatePredefinedUserData();
                let loginPayload = {
                    visitorId: this.visitorId,
                    languageCode: this.language.code,
                    siteId: this.site._id,
                    visitingPage: this.visitingPage,
                    loginless: true,
                    sessionId,
                    connectingUserId,
                    ...preDefined,
                };
                if (this.customLoginFormEnabled && lastCustomLogin) {
                    const { customLoginFormValues } = lastCustomLogin;
                    if (Array.isArray(customLoginFormValues)) {
                        loginPayload = {
                            ...loginPayload,
                            customLoginForm: true,
                            customLoginFormValues: customLoginFormValues,
                        };
                    }
                }
                if (this.loginFormRequired && !this.customLoginFormEnabled && lastLoginData) {
                    loginPayload = {
                        ...loginPayload,
                        ...lastLoginData,
                    };
                }
                if (!!preDefined.email || !!preDefined.fullname || !!preDefined.subjectId) {
                    loginPayload = {
                        ...loginPayload,
                        ...preDefined,
                    };
                }
                this.sendLogin(loginPayload, "startChatImmediately");
                sendOpenBubbleRequest();
            }
        },
        bindSocketEvents() {
            log.info("bindSocketEvents started");
            if (this.$socket) {
                log.debug("socket.io instance", this.$socket);
                const originalOnEventFunc = this.$socket.onevent;
                const self = this;

                this.$socket.onevent = function () {
                    try {
                        const payload = arguments[0].data;
                        let cmd = payload[0];
                        const maybeSiteId = cmd.split("-").length > 1 ? _.last(cmd.split("-")) : undefined;
                        cmd = _.head(cmd.split("-"));
                        log.info("cmd", { payload });
                        if (maybeSiteId && maybeSiteId !== self.site._id) {
                            log.error("SHOULD NOT BE HAPPEN, other site socket message received");
                            return;
                        }
                        if (cmd === "visitor_connect") {
                            const visitor = payload[1];
                            self.setVisitorInfo(visitor);
                            if (!self.visitorId) {
                                setPersistenceData("visitorId", visitor._id);
                                self.setVisitorId(visitor._id);
                            } else {
                                if (self.visitorId !== visitor._id) {
                                    setPersistenceData("visitorId", visitor._id);
                                    self.setVisitorId(visitor._id);
                                }
                            }
                            publishMessage(SystemMessageTypes.VISITOR_ID_RECEIVED, {
                                visitorId: visitor._id,
                            });
                            publishMessage("SET_VISITOR_LANG", { lang: visitor.preferredLang });
                        } else if (cmd === "visitor_login") {
                            self.resetState();
                            const conversation = payload[1];
                            setPersistenceData("conversationId", conversation._id);
                            self.conversationId = conversation._id;
                            self.conversation = conversation;
                            self.setVisitorIsLoggedIn(true);
                            self.setVisitorIsLogging(false);
                            self.startHealthCheckTimer();
                            self.loadOldMessages();
                            setTimeout(self.loadOldMessages, 6000);
                            const visitorId = _.get(conversation, "visitor._id");
                            if (visitorId && visitorId !== self.visitorId) {
                                log.error("unmatched visitor id detected");
                                setPersistenceData("visitorId", visitorId);
                                self.visitorId = visitorId;
                            }
                            publishMessage("CHAT_STARTED", {});
                            sendCommandToParentWindow("visitor-connected");
                        } else if (cmd === "settings_updated") {
                            const settings = payload[1];
                            log("SETTINGS", settings);

                            self.setSettings(settings);
                            sendCommandToParentWindow("settings-updated", settings);
                        } else if (cmd === "queue_number") {
                            const { queueNumber } = payload[1];
                            self.setQueueNumber(queueNumber);
                        } else if (cmd === "conversation_visitor_info_updated") {
                            let { fullname, email } = payload[1];
                            if (email === "visitor@supsis.live") email = undefined;
                            setPersistenceData("pre-defined-user-data", JSON.stringify({ email, fullname }), true);
                        } else if (cmd === "chat_agents") {
                            const { agents } = payload[1];
                            addSocketMessage(cmd, agents);
                            if (agents.length) self.setChatAgents(agents);
                        } else if (cmd === "offline_mode_updated") {
                            const { offlineMode } = payload[1];
                            self.startTrigger(offlineMode);
                            Trigger.setOfflineMode(offlineMode);
                            const hideBubble =
                                self.settings.hideBubble ||
                                (!!self.settings && self.settings.autoHideBubble && offlineMode);
                            if (hideBubble) {
                                sendCommandToParentWindow("removeOpenButton");
                            } else {
                                log("connected sending to parent from 3");
                                sendCommandToParentWindow("connected");
                            }
                            self.offlineModeUpdated({ offlineMode });
                        } else if (cmd === "custom-login-form-updated") {
                            const { inputs } = payload[1];
                            self.multiSetInputs(inputs);
                        } else if (cmd === "died") {
                            self.reSendConnectMessages();
                        } else if (cmd === "user_lang_change") {
                            self.updateUserLang(payload[1]);
                        } else if (cmd === "conversation_destroyed") {
                            self.loadOldMessages(false);
                            addSocketMessage(cmd, payload[1]);
                            sendCommandToParentWindow("visitor-disconnected");
                        } else if (cmd === "message") {
                            const data = payload[1];
                            const disconnectedNeeded = ["user-destroy", "visitor-destroy", "byebye-message"].includes(
                                data.systemMessageType
                            );

                            if (data.systemMessageType === "user-login") {
                                setPersistenceData("conversationId", data.conversationId);
                                self.conversationId = data.conversationId;
                            }
                            if (self.conversationId) self.addMessage(data);
                            addSocketMessage(cmd, payload[1]);
                            if (disconnectedNeeded) {
                                setTimeout(() => {
                                    self.disconnect(false);
                                }, 2000);
                            }
                        } else if (cmd === "visitor_lang_change") {
                            publishMessage("SET_VISITOR_LANG", { lang: payload[1].preferredLang });
                        } else if (cmd === "catalog-content-updated" || cmd === "catalog-content-added") {
                            self.changeCatalogContent(payload[1]);
                        } else if (cmd === "catalog-category-updated" || cmd === "catalog-category-added") {
                            self.changeCatalogCategory(payload[1]);
                        } else if (cmd === "toggle_video_call") {
                            log("toggle video call ", payload[1]);
                            self.mutRemoteMicEnabled(payload[1].mic);
                            self.mutRemoteVideoEnabled(payload[1].video);
                        } else if (cmd === "video_call_closed") {
                            self.cancelVideoCall(false);
                        } else if (cmd === "start_chat_immediately") {
                            log("start_chat_immediately received", payload[1]);
                            const userId = payload[1]["userId"];
                            self.startChatImmediately(userId);
                        } else if (cmd === "chat_started") {
                            log("chat_started received");
                            window.location.reload();
                        } else if (cmd === "agent_leave_from_chat") {
                            const { userId } = payload[1];
                            self.updateAgents(userId);
                        } else if (cmd === "agent_added_to_chat") {
                            const { userId } = payload[1];
                            self.updateAgents(userId, true);
                        } else if (cmd.startsWith("chatbot/v2")) {
                            self.handleChatbotEvents(cmd, payload[1]);
                        } else if (cmd.startsWith("akinon")) {
                            const service = AkinonScrapperApiClient.create();
                            const data = payload[1];
                            log("akinon message received", cmd, data);
                            if (cmd === "akinon_get_orders") {
                                service.getOrders(data.siteId, data.visitorId, data.conversationId, data.instanceId);
                            }
                        } else if (cmd === "visitor_location_response") {
                            log.info(cmd, payload[1]);
                            self.setVisitorLocation(payload[1]);
                        } else if (cmd === "ping") {
                            sendSocketMessage("visitor pong", {
                                conversationId: self.conversationId,
                                siteId: self.site._id,
                                visitorId: self.visitorId,
                            });
                        } else {
                            const otherCommands = [
                                "video_call_request",
                                "webrtc",
                                "video_call_closed",
                                "visitor_url_changed",
                                "agent_added_to_chat",
                                "translate_message",
                                "conversation_inactive_status_changed",
                                "list_countries_response",
                                "find_states_response",
                                "find_cities_response",
                            ];
                            if (!otherCommands.includes(cmd)) {
                                log.error("unhandled socket message: " + cmd);
                            }
                            addSocketMessage(cmd, payload[1]);
                        }
                    } catch (e) {
                        log.error("error", e);
                    } finally {
                        originalOnEventFunc.call(self.$socket, ...arguments);
                    }
                };
                this.$socket.on("connect", () => {
                    log("socket connected -->");
                    this.setConnectionStatus(false);
                    this.sendVisitorConnectRequest();
                    this.loadOldMessages();
                    this.socketTryCount = 0;
                });
                this.$socket.on("disconnect", () => {
                    log("socket disconnected -->");
                    this.setConnectionStatus(true);
                    this.socketConnected = false;
                    if (this.connectedSocketServerId) {
                        markFailedSocketServer(this.connectedSocketServerId);
                        setTimeout(() => {
                            this.startSocketIOConnection();
                        }, Math.ceil(Math.random() * 5 * 1000));
                    }
                });
            }
        },
        startSocketIOConnection() {
            log.info("startSocketIOConnection started");
            const candidateServer = getNextSocketServerCandidate(false);
            if (candidateServer) {
                log.warn("current candidate server", candidateServer, this.$socket);
                this.connectedSocketServerId = candidateServer.instanceId;
                const socketIOAddress = candidateServer.socketIOAddress;
                const transportConfigs =
                    !this.tabVisible && this.lastTabHiddenTS && Date.now() - this.lastTabHiddenTS >= 5000
                        ? {
                              transports: ["polling"],
                              upgrade: true,
                              autoConnect: true,
                              reconnect: false,
                              reconnection: false,
                          }
                        : {
                              transports: ["websocket", "polling"],
                              upgrade: false,
                              autoConnect: true,
                              reconnect: false,
                              reconnection: false,
                          };

                const socket = io.connect(socketIOAddress, {
                    query: "source=visitor&siteId=" + this.site._id,
                    secure: false,
                    rejectUnauthorized: false,
                    timeout: 30000,
                    ...transportConfigs,
                });

                log.warn("socket->", socket);
                window.socket = socket;
                this.$socket = socket;
                updateSocketRef(socket);

                this.bindSocketEvents();

                this.$socket.on("reconnect", (e) => {
                    log.warn("socket.io event reconnect", e);
                });
                this.$socket.on("reconnect_attempt", (e) => {
                    log.warn("socket.io event reconnect_attempt", e);
                });
                this.$socket.on("connect_error", (e) => {
                    log.warn("socket.io event connect_error", e);
                    markFailedSocketServer(candidateServer.instanceId);
                    setTimeout(() => {
                        this.startSocketIOConnection();
                    }, Math.ceil(Math.random() * 3000) + 1000);
                });

                this.$socket.on("reconnect_error", (e) => {
                    log.warn("socket.io event reconnect_error", e);
                });
                this.$socket.on("reconnect_failed", (e) => {
                    log.warn("socket.io event reconnect_failed", e);
                });
                this.$socket.on("error", (e, a) => {
                    console.error(e);
                    log.warn("socket.io error", e, a);
                    markFailedSocketServer(candidateServer.instanceId);
                    setTimeout(() => {
                        this.startSocketIOConnection();
                    }, Math.ceil(Math.random() * 3000) + 1000);
                });
                sendSocketMessage("visitor location", {
                    siteId: this.site._id,
                });
                // this.init();
                log.debug("this.$socket", this.$socket, this);
            } else {
                log.warn("There is no socket server candidate , trying later");
                setTimeout(() => {
                    this.startSocketIOConnection();
                }, Math.min(3000, this.socketTryCount * 100));
                this.socketTryCount += 1;
            }
        },
        cancelVideoCall(signal = true) {
            log("cancelVideoCall", signal);

            if (signal)
                sendSocketMessage("visitor video-call-close", {
                    siteId: this.site._id,
                    visitorId: this.visitorId,
                    userId: this.currentVC,
                    conversationId: this.conversationId,
                    sendMessage: signal,
                    isSoundOnly: this.isSoundOnly,
                });
            this.setVideoCall(null);
            this.closeConnection();
            sendCommandToParentWindow("wide-visitor-screen", false);
            this.setVideoCallMode(false);
        },
        initializeTheme() {
            const settings = JSON.parse(getPersistenceData("settings"));
            log("initializeTheme", settings);
            if (settings && settings.chatCustomizationSettings) {
                //todo:: dynamic setting set

                setThemeColors(settings.chatCustomizationSettings);
            }
        },
        sendPendingMessages() {
            log("sendPendingMessages", this.pendingMessages);
            this.pendingMessages.forEach((message) => {
                this.sendMessage(message.description, true);
            });
            this.pendingMessages = [];
            removePersistenceData("pending-messages");
        },
        reSendConnectMessages() {
            log("reSendConnectMessages running");
            sendSocketMessage("visitor connect", {
                visitorId: this.visitorId,
                conversationId: this.conversationId,
                siteId: this.site._id,
                sessionId,
                version,
            });
        },
        async autoLogin(initialMessage, loginData) {
            log("autoLogin", { initialMessage, loginData });
            const isValid =
                !this.connected && !this.conversationId && !this.visitorIsLogging && !this.visitorIsLoggedIn;

            if (isValid) {
                const promise = this.socketIsOpen
                    ? Promise.resolve(null)
                    : systemMessageStream
                          .pipe(
                              filter((x) => x.type === SystemMessageTypes.VISITOR_ID_RECEIVED),
                              first()
                          )
                          .toPromise();

                promise.then((x) => {
                    this.toggleAfterActions(false);
                    this.sendMessage(initialMessage, false, false, true, loginData);
                });
            } else {
                log.warn("autoLogin called but already connected", {
                    connected: this.connected,
                    conversationId: this.conversationId,
                    visitorIsLogging: this.visitorIsLogging,
                    visitorIsLoggedIn: this.visitorIsLoggedIn,
                });
            }
        },
        startTrigger(offlineMode) {
            if (!!this.settings.triggers && !!this.settings.triggers.length && !!this.settings.isEnabledTriggers)
                Trigger.create(this.settings.triggers, this.language.code, offlineMode);
        },
        closeOpenMoment() {
            log("closeOpenMoment");
            this.openMoment.visible = false;
            this.openMoment = _.cloneDeep(this.openMoment);
        },
        handleChatbotEvents(cmd, data) {
            log("handleChatbotEvents", { cmd, data });
            if (cmd === "chatbot/v2 action close") {
                this.openMoment.visible = false;
            } else if (cmd === "chatbot/v2 action") {
                const { payload, type } = data;
                log("handleChatbotEvents", { payload, type });
                switch (type) {
                    case "OPEN_URL":
                        log("handleChatbotEvents OPEN_URL", payload.url);
                        window.open(payload.url, "_blank");
                        break;

                    case "PHONE_CALL":
                        log("handleChatbotEvents PHONE_CALL", payload.phoneNumber);
                        sendCommandToParentWindow("phone-call", { phoneNumber: payload.phoneNumber });
                        break;
                    case "OPEN_MOMENT":
                        log("handleChatbotEvents OPEN_MOMENT", payload.url);
                        this.openMoment.url = payload.url;
                        this.openMoment.visible = true;
                        this.openMoment.size = payload.windowSize;
                        this.openMoment = _.cloneDeep(this.openMoment);
                        log("handleChatbotEvents OPEN_MOMENT state", this.openMoment);
                        break;
                    case "RESERVATION_OPEN_MOMENT":
                        this.openMoment.url = payload.url;
                        this.openMoment.visible = true;
                        this.openMoment.size = payload.windowSize;
                        this.openMoment = _.cloneDeep(this.openMoment);
                        break;
                }
            }
        },
    },
    async mounted() {
        await this.init(true);
        const self = this;
        const urlChangeSub = reportUrlJob().subscribe((url) => {
            Sentry.setTags({
                hostUrl: url,
            });
            this.setVisitingPage(url);

            if (this.visitorId) {
                sendSocketMessage("visitor url changed", {
                    visitorId: this.visitorId,
                    conversationId: this.conversationId,
                    siteId: this.site._id,
                    url: url,
                });
            }
        });
        this.urlChangeSub = urlChangeSub;

        const infoChangeSub = reportInfoJob().subscribe(({ title, url }) => {
            Trigger.setTitle(title);
            Trigger.setURL(url);
        });

        const videoCallSub = socketMessageStream
            .pipe(
                filter((x) => x.cmd === "video_call_request"),
                map((x) => x.data)
            )
            .subscribe(async (data) => {
                const { userId, soundOnly, screenOnly } = data;
                log.info("video_call_request", data);
                if (!userId) {
                    log.error("video_call_request received but user id not found", data);
                }
                let config = {
                    visitorId: this.visitorId,
                    secure: true,
                    ssl: true,
                    autocam: true,
                    isMobile: data["isMobile"],
                };
                if (!screenOnly) {
                    let media = {
                        video: soundOnly ? false : { facingMode: "user" },
                        audio: true,
                    };
                    config = { media, ...config };
                }
                const phone = PHONE_UTILS.getPhone() || PHONE(config);
                phone.configUpdate(config);
                PHONE_UTILS.setPhoneInstance(phone);
                if (soundOnly) {
                    this.setSoundOnly(true);
                } else {
                    this.setSoundOnly(false);
                }
                if (screenOnly) {
                    this.setScreenOnly(true);
                }
                window.px = phone;
                await this.setCalling(userId);
                this.setVideoCallMode(true);
            });

        const tabVisibilitySub = systemMessageStream
            .pipe(
                filter((x) => x.type === SystemMessageTypes.TAB_VISIBILITY_CHANGE),
                map((x) => x.payload)
            )
            .subscribe((payload) => {
                Sentry.setTags({
                    tabVisible: payload,
                });
                this.changeTabVisibilityChange(payload.visible);
            });
        const restartNeedSub = systemMessageStream
            .pipe(filter((x) => x.type === SystemMessageTypes.RESTART_NEEDED))
            .subscribe((payload) => {
                this.resetState();
                this.chatClosed();
            });

        const autoLoginSub = systemMessageStream
            .pipe(filter((x) => x.type === SystemMessageTypes.AUTO_LOGIN))
            .subscribe(({ payload }) => {
                log("autoLogin detected");
                this.autoLogin(payload.initialMessage, payload.loginData);
            });

        const sendLoginSub = systemMessageStream
            .pipe(filter((x) => x.type === SystemMessageTypes.SEND_LOGIN))
            .subscribe(({ payload }) => {
                this.sendLogin(payload, "sendLoginSub");
            });

        const contactPropertyDataSub = systemMessageStream
            .pipe(filter((e) => e.type === "contact_property_set"))
            .subscribe(({ payload }) => {
                log("setVisitorContactProperty sub detected");
                this.setVisitorContactProperty(payload);
            });

        const vipCustomerSdkSub = systemMessageStream
            .pipe(filter((e) => e.type === "set_vip_customer"))
            .subscribe(({ payload }) => {
                log("setVipCustomer sub detected");
                this.setVipCustomer(payload);
            });

        const pauseResumeTriggerSub = parentWindowMessageStream
            .pipe(filter((x) => ["iframe-open", "maximize", "iframe-minimize"].includes(x.command)))
            .subscribe(({ command }) => {
                if (command === "iframe-minimize") {
                    Trigger.resume();
                } else {
                    Trigger.pause();
                }
            });

        const destroyedChatSub = socketMessageStream
            .pipe(
                filter((x) => x.cmd === "conversation_destroyed"),
                map((x) => x.data)
            )
            .subscribe((x) => {
                this.disconnect(false);
            });

        this.cancels.push(() => {
            tabVisibilitySub.unsubscribe();
            videoCallSub.unsubscribe();
            urlChangeSub.unsubscribe();
            restartNeedSub.unsubscribe();
            autoLoginSub.unsubscribe();
            sendLoginSub.unsubscribe();
            infoChangeSub.unsubscribe();
            pauseResumeTriggerSub.unsubscribe();
            destroyedChatSub.unsubscribe();
            contactPropertyDataSub.unsubscribe();
        });

        this.pendingMessages.forEach((x) => this.addMessage(x));

        log("mounted", { settings: this.settings });
        window.addEventListener("click", () => {
            if (!this.visitorInteracted) {
                this.setVisitorInteracted();
            }
        });

        const queryParams = this.calculateQueryParams();
        store.dispatch("chat/setPreDefinedUserData", {
            ...queryParams,
        });
    },
    beforeDestroy() {
        log("beforeDestroy called");
        this.cancels.forEach((x) => x());
    },
    watch: {
        newVersion(isActive) {
            log("newVersion changed", isActive);
            if (isActive) {
                document.body.classList.remove("v1");
                document.body.classList.add("v2");
            }
            this.initializeTheme();
        },
        socketTryCount(newValue, oldValue) {
            // if (newValue >= 10) {
            //     log.warn("SOCKET TRY COUNT reached to 10. Offline Mode Activating");
            //     this.offlineModeUpdated({ offlineMode: true });
            // }
        },
        visitorId(newValue, oldValue) {
            log("visitorId changed", { newValue, oldValue });
            Sentry.setTags({
                visitorId: newValue,
            });
            if (newValue) {
                requestHostUrl().then((url) => {
                    sendSocketMessage("visitor url changed", {
                        visitorId: newValue,
                        conversationId: this.conversationId,
                        siteId: this.site._id,
                        url: url,
                    });
                });
            }
        },
        async socketIsOpen(newValue) {
            log("socketIsOpen state changed", newValue, {
                isLogging: this.visitorIsLogging,
            });
            if (newValue === false) {
                if (this.visitorIsLogging) {
                    this.setVisitorIsLogging(false);
                    this.setVisitorIsLoggedIn(false);
                }
            } else {
                if (
                    !this.connected &&
                    this.pendingMessages.length &&
                    this.visitorIsLogging === false &&
                    this.visitorIsLoggedIn === false
                ) {
                    if (!this.visitorId) return;
                    if (!this.site) return;
                    this.pendingMessages.forEach((msg) => this.addMessage(msg));
                    let preDefined = await this.calculatePredefinedUserData();
                    const loginPayload = {
                        visitorId: this.visitorId,
                        languageCode: this.language.code,
                        siteId: this.site._id,
                        visitingPage: this.visitingPage,
                        loginless: true,
                        sessionId,
                        ...preDefined,
                    };
                    this.sendLogin(loginPayload, "socketIsOpen");
                }
            }
        },
        chatAgents(newValue) {
            if (Array.isArray(newValue) && newValue.length > 0) {
                this.setQueueNumber(undefined);
            }
        },
        async connected(newValue) {
            log("connected changed", {
                newValue,
                conversationId: this.conversationId,
                settings: this.settings,
            });
            if (newValue && this.pendingMessages.length) {
                this.sendPendingMessages();
            }
            if (newValue === true && this.conversationId && _.get(this.conversation, "status", "") === "chatbot") {
                const agents = await waitForChatAgents().catch((x) => Promise.resolve([]));
                log("connected changed agents", agents);
                if (!agents.length && !!this.conversation && this.conversation.status === "chatbot") {
                    this.setChatbotActiveState(true);
                    const chatbotName =
                        _.get(this.settings, "chatbotName", CONFIG.CHATBOT_NAMES.default) ||
                        _.get(CONFIG.CHATBOT_NAMES, this.site._id, CONFIG.CHATBOT_NAMES.default) ||
                        CONFIG.CHATBOT_NAMES.default;
                    this.setChatAgents([
                        {
                            name: chatbotName,
                            _id: "chatbot",
                        },
                    ]);
                }
                if (this.botDestroyer) this.botDestroyer.unsubscribe();
                this.botDestroyer = systemMessageStream
                    .pipe(
                        filter((x) => {
                            return x.type === "bot-completed" && x.payload.conversationId === this.conversationId;
                        }),
                        first()
                    )
                    .subscribe((x) => {
                        const virtualAgent = this.chatAgents.find((x) => x._id === "chatbot");
                        if (virtualAgent) {
                            this.setChatAgents([]);
                        }
                    });
            }
            if (this.conversationId) {
                Sentry.setTags({
                    conversationId: this.conversationId,
                });
            }
        },
        settings(newSetting) {
            //todo:: dynamic setting set
            setThemeColors(newSetting.chatCustomizationSettings);
            if (newSetting.kmkAutoDepartmentBind) {
                requestKMKSellerId();
                if (this.lastKMKTrackerSub) {
                    this.lastKMKTrackerSub.unsubscribe();
                    this.lastKMKTrackerSub = undefined;
                }
                const tracker = trackKmkSellerId();
                this.lastKMKTrackerSub = tracker;
                this.cancels.push(() => tracker.unsubscribe());
            }

            if (this.newVersion) {
                const color = _.get(
                    this.settings,
                    "chatCustomizationSettings.versionV2Theme.colors.backgroundColorFirst"
                );
                sendCommandToParentWindow("visible-window-bg", { color });
            }
        },
        language() {
            Trigger.setLang(this.language.code);
        },
        site: {
            handler(newValue) {
                if (newValue && this.site && this.site._id != null) {
                    Sentry.setTags({
                        siteId: this.site._id,
                        siteName: this.site.alias,
                        partner: _.get(this.site, "partner.name"),
                    });
                }
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" src="./assets/scss/components/v1/App.vue.scss" />
<style lang="scss" src="./assets/scss/components/v2/App.vue.scss" />



// WEBPACK FOOTER //
// src/App.vue